/**
 * Inline `<thinking>`-markup extraction for streamed assistant text. Pure TS —
 * lives in the engine so the accumulator's fold can use it without a runtime
 * dependency on the React-importing chat barrels; `utils/chat.ts` re-exports
 * these for the legacy call sites.
 */

// The canonical <think>/</think> tag pair, shared by the table below and the
// incremental scan further down so the two extraction paths can't drift. A
// new model's tags get wired in exactly here.
export const THINKING_START_TAG = '<think>'
export const THINKING_END_TAG = '</think>'

// Thinking tags for different models that are used to separate the thinking from the response
export const thinkingTags = {
  'deepseek-r1': {
    start: new RegExp(THINKING_START_TAG),
    end: new RegExp(THINKING_END_TAG),
    regex: new RegExp(`${THINKING_START_TAG}(.*?)${THINKING_END_TAG}`),
  },
  // Add more models here
}

export const extractThinkingContent = (text: string, _modelId?: string): { thinking: string; response: string } => {
  if (!text) {
    return { thinking: '', response: text }
  }

  // Find which thinking tag format to use based on model
  let thinkingPattern: RegExp | null = null

  // First, check for known model-specific patterns
  for (const [, tags] of Object.entries(thinkingTags)) {
    if (text.match(tags.start) && text.match(tags.end)) {
      thinkingPattern = new RegExp(tags.regex.source, 'gs') // global and dotall flags
      break
    }
  }

  if (!thinkingPattern) {
    return { thinking: '', response: text }
  }

  // Extract all thinking content
  const thinkingMatches = Array.from(text.matchAll(thinkingPattern))
  const thinkingContent = thinkingMatches
    .map((match) => match[1])
    .join('\n\n')
    .trim()

  // Remove thinking tags from the response
  const responseContent = text.replace(thinkingPattern, '').trim()

  return {
    thinking: thinkingContent,
    response: responseContent,
  }
}

// Utility functions for streaming thinking content
export const hasPartialThinkingContent = (text: string): boolean => {
  if (!text) return false

  // Check for model-specific start patterns (even without end)
  for (const tags of Object.values(thinkingTags)) {
    if (text.match(tags.start)) {
      return true
    }
  }

  return false
}

// ---------------------------------------------------------------------------
// Incremental scan (HIG-5964/R4b)
//
// The extraction this replaced rescanned the full accumulated text on every
// call, so per-delta use was O(n²) over a stream. The scan below folds the same
// extraction incrementally: watermarks remember how far the text has been
// scanned, so `advanceThinkingScan` only looks at new content (plus a
// tag-length overlap for tags split across deltas). On the common no-tags path
// the result is O(1); once a block exists it still slices O(text) per delta,
// which the string-snapshot contract requires anyway — the win is dropping the
// full-text regex walk.
//
// The replaced regex extraction lives on only as this scan's differential-test
// oracle, inside thinking-scan.test.ts.
//
// The scan works on the literal tag pair above (`THINKING_START_TAG`/
// `THINKING_END_TAG`), the same pair `thinkingTags` derives its regex from —
// one source of truth for both extraction paths.
// ---------------------------------------------------------------------------

const SCAN_START_TAG = THINKING_START_TAG
const SCAN_END_TAG = THINKING_END_TAG

type ThinkingBlock = {
  /** Offset of `<think>`. */
  readonly start: number
  /** Offset just after `<think>`. */
  readonly contentStart: number
  /** Offset of `</think>`. */
  readonly contentEnd: number
  /** Offset just after `</think>`. */
  readonly end: number
}

export type ThinkingScanState = {
  /** Completed `<think>…</think>` blocks, in order. */
  readonly blocks: ReadonlyArray<ThinkingBlock>
  /** Offsets of an opened block still waiting for its end tag. */
  readonly openStart?: number
  readonly openContentStart?: number
  /** No unfound tag can begin before this offset. */
  readonly scannedTo: number
  /**
   * Whether an end tag exists anywhere — tracked independently of block
   * pairing because the legacy extraction keys "complete" off its presence,
   * even before any start tag.
   */
  readonly sawEndTag: boolean
  readonly endScannedTo: number
}

export const initialThinkingScan: ThinkingScanState = { blocks: [], scannedTo: 0, sawEndTag: false, endScannedTo: 0 }

/** The offset scanning for `tag` can resume from when `text` holds no full match. */
const tagWatermark = (text: string, tag: string): number => Math.max(0, text.length - tag.length + 1)

/**
 * Advance the scan over `text`, which must extend the text of the previous
 * call (the accumulator only ever appends). Only unscanned content is visited.
 */
export const advanceThinkingScan = (state: ThinkingScanState, text: string): ThinkingScanState => {
  let { openStart, openContentStart, scannedTo, sawEndTag, endScannedTo } = state
  let blocks: ReadonlyArray<ThinkingBlock> = state.blocks

  if (!sawEndTag) {
    const endIndex = text.indexOf(SCAN_END_TAG, endScannedTo)
    if (endIndex !== -1) {
      sawEndTag = true
    } else {
      endScannedTo = Math.max(endScannedTo, tagWatermark(text, SCAN_END_TAG))
    }
  }

  for (;;) {
    if (openStart === undefined) {
      const startIndex = text.indexOf(SCAN_START_TAG, scannedTo)
      if (startIndex === -1) {
        scannedTo = Math.max(scannedTo, tagWatermark(text, SCAN_START_TAG))
        break
      }
      openStart = startIndex
      openContentStart = startIndex + SCAN_START_TAG.length
      scannedTo = openContentStart
    } else {
      const endIndex = text.indexOf(SCAN_END_TAG, scannedTo)
      if (endIndex === -1) {
        scannedTo = Math.max(scannedTo, tagWatermark(text, SCAN_END_TAG))
        break
      }
      blocks = [
        ...blocks,
        {
          start: openStart,
          contentStart: openContentStart ?? openStart,
          contentEnd: endIndex,
          end: endIndex + SCAN_END_TAG.length,
        },
      ]
      openStart = undefined
      openContentStart = undefined
      scannedTo = endIndex + SCAN_END_TAG.length
    }
  }

  return {
    blocks,
    ...(openStart !== undefined ? { openStart, openContentStart } : {}),
    scannedTo,
    sawEndTag,
    endScannedTo,
  }
}

/** The scan's extraction over `text` — same output as the replaced full-text regex extraction. */
export const thinkingScanResult = (
  state: ThinkingScanState,
  text: string,
): { thinking: string; response: string; isThinkingComplete: boolean } => {
  const started = state.blocks.length > 0 || state.openStart !== undefined
  if (!text || !started) {
    return { thinking: '', response: text, isThinkingComplete: false }
  }

  if (state.sawEndTag) {
    const thinking = state.blocks
      .map((block) => text.slice(block.contentStart, block.contentEnd))
      .join('\n\n')
      .trim()
    let response = ''
    let position = 0
    for (const block of state.blocks) {
      response += text.slice(position, block.start)
      position = block.end
    }
    response += text.slice(position)
    return { thinking, response: response.trim(), isThinkingComplete: true }
  }

  // Partial: a start tag with no end tag anywhere yet.
  return {
    thinking: text.slice(state.openContentStart).trim(),
    response: text.slice(0, state.openStart).trim(),
    isThinkingComplete: false,
  }
}
